import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadSimpleIcon } from '@phosphor-icons/react/dist/csr/UploadSimple';

import { Button } from '../../design-system/primitives/Button/Button';
import { FileField } from '../../design-system/primitives/FileField/FileField';
import { Input } from '../../design-system/primitives/Input/Input';
import { Card } from '../../design-system/composites/Card/Card';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import { codesApi, type CodeImportResult } from '../../lib/api/codes';
import styles from './CodeUploadPanel.module.css';

/**
 * The bulk code upload — `POST /dispensary-codes/import`.
 *
 * ## NO PREVIEW STEP, ON PURPOSE
 *
 * Unlike `DeviceImportScreen`'s dry-run/apply pair, `codesApi.import` (see
 * that module's own docblock) only ever inserts, and a duplicate
 * `(dispensary, code, kind, type_code)` is counted and skipped, never
 * overwritten — there is nothing a preview would be guarding against, so
 * this screen has no `preview`/`apply` state machine, no `preview_token`,
 * and a single "Upload" button that fires the real write directly. Modelling
 * a dry run here would imply a step the API does not have (task 7's brief,
 * §5).
 *
 * ## `rejected` is a keyed OBJECT — every row shown, with its row number
 *
 * `CodeImportResult.rejected` is `Record<string, string>`, 1-based row
 * number to reason, `{}` when nothing failed (`codes.ts`'s own docblock).
 * `Object.entries` renders every one of them at once — a reject never blocks
 * the rest of the file, and an operator fixing a CSV needs every bad row in
 * one pass, not one error per re-upload. The row number is part of what is
 * shown (`codes.upload.rejectedRow`), because "row 1" and "row 14" are
 * different lines in the file the operator is looking at.
 */

export interface CodeUploadPanelProps {
  /** Fired once an upload completes successfully, carrying the full result
   *  — `CodeScreen`'s cue to refresh the availability summary. */
  onUploaded: (result: CodeImportResult) => void;
}

const FORBIDDEN_CODE = 'forbidden';

/** Sorted numerically — `Object.entries` on a string-keyed record does not
 *  promise numeric order, and "row 2" appearing after "row 11" would read
 *  as a shuffled file to an operator scanning top to bottom. */
function sortedRejections(rejected: Readonly<Record<string, string>>): Array<[string, string]> {
  return Object.entries(rejected).sort(([a], [b]) => Number(a) - Number(b));
}

export function CodeUploadPanel({ onUploaded }: CodeUploadPanelProps) {
  const { t } = useTranslation();

  const [dispensaryId, setDispensaryId] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [result, setResult] = useState<CodeImportResult | null>(null);

  const inFlight = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('codes.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('codes.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default: {
        const [firstField] = Object.values(error.details);
        return firstField?.[0] ?? t('codes.upload.uploadFailed');
      }
    }
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>): void {
    const chosen = event.target.files?.[0];
    if (chosen === undefined) return;
    setFileName(chosen.name);
    setFile(chosen);
    setFailure(null);
    setReference(null);
  }

  async function handleUpload(): Promise<void> {
    if (inFlight.current || file === null || dispensaryId.trim() === '') return;
    inFlight.current = true;
    setUploading(true);
    setFailure(null);
    setReference(null);

    try {
      const uploaded = await codesApi.import(file, dispensaryId.trim());
      setResult(uploaded);
      onUploaded(uploaded);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setFailure(describeFailure(error));
      setReference(error.requestId ?? null);
    } finally {
      inFlight.current = false;
      setUploading(false);
    }
  }

  const rejections = result === null ? [] : sortedRejections(result.rejected);
  const uploadDisabled = file === null || dispensaryId.trim() === '' || uploading;

  return (
    // One card: the title, the intro and the controls read on the card
    // ground, and the dispensary field shares the file row.
    <Card icon={<UploadSimpleIcon size={16} />} title={t('codes.upload.title')}>

      <p className={styles['intro']}>{t('codes.upload.intro')}</p>

      <div className={styles['fileRow']}>
        <Input
          label={t('codes.upload.fieldDispensary')}
          width="phone"
          dir="ltr"
          required
          value={dispensaryId}
          onChange={(event) => {
            setDispensaryId(event.target.value);
          }}
        />

        <FileField
          ref={fileInputRef}
          id="codeImportFile"
          label={t('codes.upload.fileLabel')}
          accept=".csv,text/csv"
          required
          fileName={fileName}
          emptyText={t('codes.upload.noFileChosen')}
          onChange={handleFileChange}
        />

        <Button
          icon={<UploadSimpleIcon size={16} />}
          loading={uploading}
          disabled={uploadDisabled}
          onClick={() => {
            void handleUpload();
          }}
        >
          {t('codes.upload.upload')}
        </Button>
      </div>

      <div className={styles['failure']} role="alert" aria-atomic="true">
        {failure === null ? null : <span>{failure}</span>}
        {reference === null ? null : (
          <span className={styles['reference']}>
            {t('auth.supportReference')}{' '}
            <bdi dir="ltr" data-testid="codeUploadRequestId">
              {reference}
            </bdi>
          </span>
        )}
      </div>

      {result === null ? null : (
        <>
          <p className={styles['success']} role="status">
            {t('codes.upload.resultSummary', {
              created: result.created,
              duplicate: result.duplicate,
              rejected: rejections.length,
            })}
          </p>

          {rejections.length === 0 ? null : (
            <div>
              <p className={styles['rejectedTitle']}>{t('codes.upload.rejectedTitle')}</p>
              <ul className={styles['rejectedList']}>
                {rejections.map(([row, reason]) => (
                  <li key={row}>{t('codes.upload.rejectedRow', { row, reason })}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
